import { LayerSet } from '../../types/static-layers.types';
import { getAppIsUkhab, getAppFeatureIsEnabled } from '../../store/selectors';

export const ukhabLayerSet: LayerSet = {
  id: 'ukhab',
  name: 'UK Habitat Layers',
  sources: {
    'ukhab-portal': {
      type: 'vector',
      url: 'mapbox://natapp.ukhab-portal'
    },
    'ukhab-portal-v2': {
      type: 'vector',
      url: 'mapbox://natapp.ukhab-portal-v2'
    },
    'ukhab-portal-v3': {
      type: 'vector',
      url: 'mapbox://natapp.ukhab-portal-v3'
    }
  },
  categories: [
    {
      id: 'nca',
      label: 'NCAs, LPAs & LNRS Areas',
      sort: 1
    },
    {
      id: 'designated',
      label: 'Designated Sites',
      sort: 2,
      subcategories: [
        { id: 'england', label: 'England', sort: 1 },
        { id: 'scotland', label: 'Scotland', sort: 2 },
        { id: 'wales', label: 'Wales', sort: 3 }
      ]
    },
    {
      id: 'priority-habitat',
      label: 'Priority Habitats',
      sort: 3,
      subcategories: [
        { id: 'horticulture', label: 'Arable & Horticulture', sort: 1 },
        { id: 'coastal', label: 'Coastal', sort: 2 },
        { id: 'freshwater', label: 'Freshwater', sort: 3 },
        { id: 'grassland', label: 'Grassland', sort: 4 },
        { id: 'heathland', label: 'Heathland', sort: 5 },
        { id: 'rocks', label: 'Inland rock', sort: 6 },
        { id: 'marine', label: 'Marine', sort: 7 },
        { id: 'wetland', label: 'Wetland', sort: 8 },
        { id: 'woodland', label: 'Woodland', sort: 9 }
      ]
    }
  ],
  layers: [
    // NCA Layers
    {
      id: 'lpa-uk',
      label: 'Local Planning Authorities UK',
      color: '#fff356',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'LPA23NM', code: 'LPA23CD' },
      category: 'nca',
      sort: 1
    },
    {
      id: 'nca-england',
      label: 'National Character Areas England',
      color: '#4A8FBD',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { name: 'nca_name', code: 'jcacode' },
      category: 'nca',
      sort: 2
    },
    {
      id: 'lnrs-england',
      label: 'Local Nature Recovery Strategy Areas',
      shortLabel: 'LNRS Areas',
      color: '#2a9d8f',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { name: 'Name', authority: 'Resp_Auth', status: 'Status' },
      category: 'nca',
      sort: 3
    },

    // Designated Sites - England
    {
      id: 'sssi-england',
      label: 'SSSI England',
      color: '#f00',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'sssi_name', status: 'status', reference: 'reference' },
      category: 'designated',
      subcategory: 'england',
      sort: 10
    },
    {
      id: 'aonb-england',
      label: 'AONB England',
      color: '#ff00ff',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'name' },
      category: 'designated',
      subcategory: 'england',
      sort: 11
    },
    {
      id: 'ramsar-england',
      label: 'Ramsar England',
      color: '#ff6600',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'name', status: 'status', code: 'code' },
      category: 'designated',
      subcategory: 'england',
      sort: 12
    },
    {
      id: 'nnr-england',
      label: 'National Nature Reserves England',
      color: '#00ffff',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'nnr_name', status: 'status', reference: 'reference' },
      category: 'designated',
      subcategory: 'england',
      sort: 13
    },
    {
      id: 'lnr-england',
      label: 'Local Nature Reserves England',
      color: '#ffff99',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'lnr_name', status: 'status', reference: 'reference' },
      category: 'designated',
      subcategory: 'england',
      sort: 14
    },
    {
      id: 'sac-england',
      label: 'Special Areas of Conservation England',
      color: '#ffb3e6',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'sac_name', status: 'status', code: 'sac_code' },
      category: 'designated',
      subcategory: 'england',
      sort: 15
    },
    {
      id: 'spa-england',
      label: 'Special Protection Areas England',
      color: '#e6e6e6',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'spa_name', status: 'status', code: 'spa_code' },
      category: 'designated',
      subcategory: 'england',
      sort: 16
    },
    {
      id: 'br-england',
      label: 'Biosphere Reserves England',
      color: '#f4a261',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'name', status: 'status' },
      category: 'designated',
      subcategory: 'england',
      sort: 17
    },

    // Designated Sites - Scotland
    {
      id: 'sssi-scotland',
      label: 'SSSI Scotland',
      color: '#0f0',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 20
    },
    {
      id: 'ramsar-scotland',
      label: 'Ramsar Scotland',
      color: '#d16d8a',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS', code: 'PA_CODE' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 21
    },
    {
      id: 'nnr-scotland',
      label: 'National Nature Reserves Scotland',
      color: '#800080',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS', code: 'PA_CODE' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 22
    },
    {
      id: 'sac-scotland',
      label: 'Special Areas of Conservation Scotland',
      color: '#b3b3ff',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS', code: 'PA_CODE' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 23
    },
    {
      id: 'spa-scotland',
      label: 'Special Protection Areas Scotland',
      color: '#f2c94c',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS', code: 'PA_CODE' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 24
    },
    {
      id: 'br-scotland',
      label: 'Biosphere Reserves Scotland',
      color: '#e76f51',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NAME', status: 'STATUS', code: 'PA_CODE' },
      category: 'designated',
      subcategory: 'scotland',
      sort: 25
    },

    // Designated Sites - Wales
    {
      id: 'sssi-wales',
      label: 'SSSI Wales',
      color: '#0000ff',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'sssi_name', code: 'sssi_code', status: 'status' },
      category: 'designated',
      subcategory: 'wales',
      sort: 30
    },
    {
      id: 'ramsar-wales',
      label: 'Ramsar Wales',
      color: '#ff007f',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'RAM_name', code: 'RAM_code' },
      category: 'designated',
      subcategory: 'wales',
      sort: 31
    },
    {
      id: 'nnr-wales',
      label: 'National Nature Reserves Wales',
      color: '#5b8c7e',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'NNR_Name', code: 'NNR_Code' },
      category: 'designated',
      subcategory: 'wales',
      sort: 32
    },
    {
      id: 'sac-wales',
      label: 'Special Areas of Conservation Wales',
      color: '#c2f0c2',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'SAC_name', status: 'Status', code: 'Site_code' },
      category: 'designated',
      subcategory: 'wales',
      sort: 33
    },
    {
      id: 'spa-wales',
      label: 'Special Protection Areas Wales',
      color: '#c2c2f0',
      source: 'ukhab-portal',
      layerType: 'static',
      properties: { name: 'SPA_Name', code: 'SPA_code' },
      category: 'designated',
      subcategory: 'wales',
      sort: 34
    },
    {
      id: 'br-wales',
      label: 'Biosphere Reserves Wales',
      color: '#264653',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { name: 'bios_name' },
      category: 'designated',
      subcategory: 'wales',
      sort: 35
    },

    // Priority Habitats - Horticulture
    {
      id: 'traditional-rchards',
      label: 'Traditional orchards',
      color: '#6e9a2a',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'horticulture',
      sort: 100
    },

    // Priority Habitats - Coastal
    {
      id: 'coastal-floodplain-grazing-marsh',
      label: 'Coastal & floodplain grazing marsh',
      color: '#5a85ee',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'coastal',
      sort: 110
    },
    {
      id: 'coastal-saltmarsh',
      label: 'Coastal saltmarsh',
      color: '#6fae4b',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'coastal',
      sort: 111
    },
    {
      id: 'coastal-sand-dunes',
      label: 'Coastal sand dunes',
      color: '#db912d',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'coastal',
      sort: 112
    },
    {
      id: 'coastal-vegetated-shingle',
      label: 'Coastal vegetated shingle',
      color: '#7cd6af',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'coastal',
      sort: 113
    },
    {
      id: 'maritime-cliff-slope',
      label: 'Maritime cliff & slope',
      color: '#34557b',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'coastal',
      sort: 114
    },

    // Priority Habitats - Freshwater
    {
      id: 'lakes',
      label: 'Lakes',
      color: '#7ad9fb',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'freshwater',
      sort: 120
    },
    {
      id: 'ponds',
      label: 'Ponds',
      color: '#7ad9fb',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'freshwater',
      sort: 121
    },

    // Priority Habitats - Grassland
    {
      id: 'calaminarian-grassland',
      label: 'Calaminarian grassland',
      color: '#f15828',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 130
    },
    {
      id: 'lowland-calcareous-grassland',
      label: 'Lowland calcareous grassland',
      color: '#7e7c44',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 131
    },
    {
      id: 'lowland-dry-acid-grassland',
      label: 'Lowland dry acid grassland',
      color: '#f8b9b7',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 132
    },
    {
      id: 'lowland-meadows',
      label: 'Lowland meadows',
      color: '#9ddd3d',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 133
    },
    {
      id: 'upland-calcareous-grassland',
      label: 'Upland calcareous grassland',
      color: '#fbcd7f',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 134
    },
    {
      id: 'upland-hay-meadows',
      label: 'Upland hay meadows',
      color: '#e7abc7',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 135
    },
    {
      id: 'purple-moor-grass-rush-pastures',
      label: 'Purple moor grass & rush pastures',
      color: '#932cd9',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'grassland',
      sort: 136
    },

    // Priority Habitats - Heathland
    {
      id: 'lowland-heathland',
      label: 'Lowland heathland',
      color: '#debafa',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'heathland',
      sort: 140
    },
    {
      id: 'upland-heathland',
      label: 'Upland heathland',
      color: '#67dc3b',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'heathland',
      sort: 141
    },
    {
      id: 'mountain-heath-willow-scrub',
      label: 'Mountain heath & willow scrub',
      color: '#6f3a55',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'heathland',
      sort: 142
    },

    // Priority Habitats - Rocks
    {
      id: 'limestone-pavements',
      label: 'Limestone pavements',
      color: '#f6a17a',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'rocks',
      sort: 150
    },

    // Priority Habitats - Marine
    {
      id: 'mudflats',
      label: 'Mudflats',
      color: '#734d31',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'marine',
      sort: 160
    },
    {
      id: 'saline-lagoons',
      label: 'Saline lagoons',
      color: '#c84f76',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'marine',
      sort: 161
    },

    // Priority Habitats - Wetland
    {
      id: 'blanket-bog',
      label: 'Blanket Bog',
      color: '#ede0ba',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'wetland',
      sort: 170
    },
    {
      id: 'lowland-fens',
      label: 'Lowland fens',
      color: '#c87f7e',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'wetland',
      sort: 171
    },
    {
      id: 'lowland-raised-bog',
      label: 'Lowland raised bog',
      color: '#d49763',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'wetland',
      sort: 172
    },
    {
      id: 'reedbeds',
      label: 'Reedbeds',
      color: '#367267',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'wetland',
      sort: 173
    },
    {
      id: 'upland-flushes-fens-and-swamps',
      label: 'Upland flushes, fens & swamps',
      color: '#9f9c2d',
      source: 'ukhab-portal-v3',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'wetland',
      sort: 174
    },

    // Priority Habitats - Woodland
    {
      id: 'deciduous-woodland',
      label: 'Deciduous woodland',
      color: '#73a76e',
      source: 'ukhab-portal-v2',
      layerType: 'static',
      properties: { source: 'primsource', code: 'habcodes' },
      category: 'priority-habitat',
      subcategory: 'woodland',
      sort: 180
    }
  ],
  isEnabled: (state) => getAppIsUkhab(state) && getAppFeatureIsEnabled('dataDrivenStyling')(state)
};

export const DATA_LAYER_SOURCES = new Set(Object.keys(ukhabLayerSet.sources));